<script setup lang="ts">
import { vMaska } from 'maska/vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRequestOTPMutation } from '@/entities/auth'
import { useCustomToast } from '@/shared/lib'
import CButton from './CButton.vue'
import CDialog from './CDialog.vue'
import CSMSCodeDialog from './CSMSCodeDialog.vue'
// v-maska="{ mask: '+7 (###) ###-##-##' }"

interface Props {
  modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'getCode': [phoneNumber: string]
  'verifyCode': [code: string, phoneNumber: string]
  'resendCode': [phoneNumber: string]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const { t } = useI18n()
const { success: showSuccess, error: showError } = useCustomToast()

// OTP mutation
const { mutate: requestOTP, isPending: isRequestingOTP }
  = useRequestOTPMutation()

const phoneNumber = ref('')
const showSMSCode = ref(false)
const sessionId = ref('')

function closeDialog() {
  isOpen.value = false
  phoneNumber.value = ''
  showSMSCode.value = false
  sessionId.value = ''
}

async function handleGetCode() {
  if (!phoneNumber.value.trim().length) {
    return
  }

  const number = phoneNumber.value.trim().replace(/^\+/, '')

  requestOTP(
    { phone_number: number },
    {
      onSuccess: (response) => {
        // Store the session_id from the response
        sessionId.value = response.data.session_id

        showSuccess({
          title: t('success'),
          message: `${t('code_sent_to_number')} ${phoneNumber.value}`,
        })
        emit('getCode', phoneNumber.value)
        // Transition to SMS code dialog
        showSMSCode.value = true
      },
      onError: (error) => {
        console.error('Error getting code:', error)
        showError({
          title: t('error'),
          message: t('failed_to_send_code_to_number'),
        })
      },
    },
  )
}

function handleVerifyCode(code: string) {
  emit('verifyCode', code, phoneNumber.value)
  closeDialog()
}

function handleResendCode(phoneNumber: string) {
  emit('resendCode', phoneNumber)
}

function goBackToPhone() {
  showSMSCode.value = false
}

function handleSMSCodeClose(value: boolean) {
  if (!value) {
    closeDialog()
  }
}
</script>

<template>
  <div>
    <!-- Phone Number Dialog -->
    <CDialog
      v-if="!showSMSCode && isOpen"
      v-model="isOpen"
      class="max-w-[480px] !w-[90%] sm:!w-[480px]"
      title="Номер телефона"
    >
      <template #header>
        <div class="flex items-center justify-between w-full">
          <h3 class="text-lg font-semibold text-text-regular">
            Номер телефона
          </h3>
          <button
            class="text-text-secondary hover:text-text-regular transition-colors"
            @click="closeDialog"
          >
            <i class="icon-close text-xl" />
          </button>
        </div>
      </template>

      <div class="flex flex-col gap-4">
        <div>
          <p class="text-sm text-text-secondary mb-2">
            Вам пришел код в Push-уведомлении Wildberries для покупателей, либо
            по СМС. Если код не пришел, запросите повторно
          </p>
          <label class="text-sm text-text-secondary mb-2 block">
            Введите номер телефона
          </label>
          <ElInput
            v-model="phoneNumber"
            v-maska="'+#############'"
            placeholder="Введите"
            size="large"
            class="w-full"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <CButton
            variant="ghost"
            class="!bg-white !border-border-light !text-text-secondary"
            @click="closeDialog"
          >
            Отмена
          </CButton>
          <CButton
            type="primary"
            :loading="isRequestingOTP"
            class="text-sm disabled:opacity-50 transition-300"
            :disabled="!phoneNumber"
            @click="handleGetCode"
          >
            <i v-if="!isRequestingOTP" class="icon-mail text-2xl mr-1" />
            Получить код
          </CButton>
        </div>
      </template>
    </CDialog>

    <!-- SMS Code Dialog -->
    <CSMSCodeDialog
      v-if="showSMSCode"
      v-model="showSMSCode"
      :phone-number="phoneNumber"
      :session-id="sessionId"
      @verify-code="handleVerifyCode"
      @resend-code="handleResendCode"
      @go-back="goBackToPhone"
      @update:model-value="handleSMSCodeClose"
    />
  </div>
</template>
